import React, { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";

import { Icon, Input, useSummaryContext } from "@braze/beacon-core";

import { updateInputDescription, updateInputParameters } from "@lib/agenticAi/sharedToolContracts";

import { useDebouncedInputValue } from "@services/useDebouncedInputValue";
import useI18nNamespace from "@services/useI18nNamespace";
import { useOperatorEntityId } from "@services/useOperatorEntityId";
import { useRegisterChangeTool } from "@services/useRegisterChangeTool";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay";
import { OperatorDescriptionWrapper } from "@components/OperatorDescriptionWrapper";

import type { BrazeInputProps } from "./types";

export const BrazeInput = forwardRef<HTMLInputElement, BrazeInputProps>(
  (
    {
      onChange,
      onBlur,
      operatorLabel,
      operatorDisabledReason,
      operatorValidate,
      disabled,
      readOnly,
      isSummary,
      placeholder,
      value,
      name,
      sensitive,
      type = "text",
      debounce = false,
      ...props
    },
    ref
  ) => {
    const { t } = useI18nNamespace("braze-input");
    const registerChangeTool = useRegisterChangeTool({ operatorDisabledReason });
    const id = useOperatorEntityId("braze-input");
    const shouldRedactValue = sensitive || type === "password";
    const summaryContext = useSummaryContext();
    const isInSummaryMode = isSummary || summaryContext.isSummary;

    // Use refs to avoid re-registering the tool when callbacks or values change.
    // This prevents infinite loops when parent components pass inline callbacks.
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;
    const operatorValidateRef = useRef(operatorValidate);
    operatorValidateRef.current = operatorValidate;

    const emitChange = useCallback(
      (next: string) => {
        onChangeRef.current?.({ target: { value: next, name } } as React.ChangeEvent<HTMLInputElement>);
      },
      [name]
    );
    // Held constant while off so the hook never schedules a draft update for the many consumers
    // that don't debounce; flipping it on adopts the live value like any external write.
    const { inputValue, onInputChange, commitNow, handleBlur } = useDebouncedInputValue({
      value: debounce ? String(value ?? "") : "",
      onChange: emitChange,
      onBlur,
      enabled: debounce,
    });
    const commitNowRef = useRef(commitNow);
    commitNowRef.current = commitNow;
    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onInputChange(event.target.value);
      },
      [onInputChange]
    );

    // What the user sees: the pending draft when debouncing, else the committed value as-is so
    // non-string consumers keep their value type.
    const renderedValue = debounce ? inputValue : value;
    const valueRef = useRef(renderedValue);
    valueRef.current = renderedValue;

    useEffect(() => {
      if (disabled || isInSummaryMode || readOnly) {
        return;
      }

      const cleanup = registerChangeTool({
        toolEntityId: id,
        name: "update_input",
        description: updateInputDescription,
        parameters: updateInputParameters,
        callback: (params: { text: string }) => {
          const rejection = operatorValidateRef.current?.(params.text);
          if (rejection) {
            throw new Error(rejection);
          }
          if (debounce) {
            // An Operator write is authoritative: it lands now and drops any pending keystroke commit.
            commitNowRef.current(params.text);
          } else {
            onChangeRef.current?.({ target: { value: params.text, name } } as React.ChangeEvent<HTMLInputElement>);
          }
          // Trigger blur after change so forms using onBlur validation mode re-validate and clear errors
          onBlurRef.current?.({ target: { value: params.text, name } } as React.FocusEvent<HTMLInputElement>);
          return shouldRedactValue ? "Input was successfully updated." : `Input was set to "${params.text}".`;
        },
        options: {
          requiredSkill: "page-controls",
          toolGroup: "Agentic Creation & Editing",
          labels: {
            ready: t("update-input-ready", "Preparing to update input"),
            running: t("update-input-running", "Updating input"),
            complete: t("update-input-complete", "Updated input"),
            failed: t("update-input-failed", "Failed to update input"),
            stopped: t("update-input-stopped", "Stopped updating input"),
          },
          requiresApproval: "yes",
          argumentsSummaryText: () => operatorLabel,
          renderApproval: ({ args: { text } }) => (
            <DiffDisplay
              title={operatorLabel}
              original={String(valueRef.current || "")}
              modified={text}
              placeholder={t("empty-placeholder", "Empty")}
            />
          ),
          toolIcon: <Icon name="pen-square" />,
        },
      });

      return () => {
        cleanup();
      };
    }, [
      registerChangeTool,
      id,
      operatorLabel,
      placeholder,
      t,
      disabled,
      isInSummaryMode,
      readOnly,
      name,
      shouldRedactValue,
      debounce,
    ]);

    const operatorDescription = useMemo(() => {
      const input = document.createElement("input");
      input.type = type;
      input.id = id;
      input.setAttribute("aria-label", operatorLabel);

      if (placeholder) {
        input.setAttribute("placeholder", placeholder);
      }

      if (disabled) {
        input.disabled = true;
      }

      if (readOnly || isInSummaryMode) {
        input.readOnly = true;
      }

      if (shouldRedactValue) {
        input.setAttribute("value", "[redacted]");
      } else if (renderedValue) {
        input.setAttribute("value", String(renderedValue));
      }

      return input.outerHTML;
    }, [
      id,
      operatorLabel,
      disabled,
      readOnly,
      isInSummaryMode,
      placeholder,
      renderedValue,
      shouldRedactValue,
      type,
    ]);

    return (
      <OperatorDescriptionWrapper description={operatorDescription}>
        <Input
          {...props}
          name={name}
          ref={ref}
          onChange={debounce ? handleInputChange : onChange}
          onBlur={debounce ? handleBlur : onBlur}
          disabled={disabled}
          readOnly={readOnly}
          isSummary={isInSummaryMode}
          placeholder={placeholder}
          type={type}
          value={renderedValue}
        />
      </OperatorDescriptionWrapper>
    );
  }
);
